import { showAlert } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { DATE_FORMATS } from '@/utils/date-formats';
import {
  useAcceptGuardianInvitationMutation,
  useDeclineGuardianInvitationMutation,
  useGuardianInvitationQuery,
} from '@/features/guardian/queries/invitations';
import {
  GuardianInvitationErrorReason,
  GuardianInvitationStatus,
  GuardianInvitationSubmissionAction,
} from '@/features/guardian/types/invitation';
import type { RootStackScreenProps } from '@/navigations/types';
import { useSessionStore } from '@/stores';
import { useNavigation } from '@react-navigation/native';
import { format, isValid, parseISO } from 'date-fns';
import { useCallback, useEffect, useMemo } from 'react';

const copy = STRINGS.guardian.coGuardianInvite;
type InvitationNavigation = RootStackScreenProps<'GuardianInvitation'>['navigation'];
type InvitationErrorParams = RootStackScreenProps<'GuardianInvitationError'>['route']['params'];

enum HttpStatus {
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,
  Conflict = 409,
}

function getApiStatus(error: unknown) {
  if (!error || typeof error !== 'object' || !('status' in error)) return 0;
  return typeof error.status === 'number' ? error.status : 0;
}

function getInvitedEmail(error: unknown) {
  if (!error || typeof error !== 'object' || !('details' in error)) return undefined;
  const details = error.details;
  if (!details || typeof details !== 'object' || !('invitedEmail' in details)) return undefined;
  return typeof details.invitedEmail === 'string' && details.invitedEmail.trim()
    ? details.invitedEmail.trim()
    : undefined;
}

function isInvitationEmailMismatch(error: unknown) {
  if (!error || typeof error !== 'object' || !('message' in error)) return false;
  const message = typeof error.message === 'string' ? error.message : '';
  return /(email|invitee|recipient).*(match|different|invited|invitation)/i.test(message);
}

export function useGuardianInvitation(token: string) {
  const navigation = useNavigation<InvitationNavigation>();
  const isInitialized = useSessionStore(state => state.isInitialized);
  const isAuthenticated = useSessionStore(state => state.isAuthenticated);
  const isSoftLoggedOut = useSessionStore(state => state.isSoftLoggedOut);
  const guardianId = useSessionStore(state => state.user?.uid);
  const userEmail = useSessionStore(state => state.user?.email) ?? '';
  const hasAuthenticatedSession = isAuthenticated && !isSoftLoggedOut;
  const invitationQuery = useGuardianInvitationQuery(token, isInitialized, hasAuthenticatedSession);
  const acceptMutation = useAcceptGuardianInvitationMutation(token, guardianId);
  const declineMutation = useDeclineGuardianInvitationMutation(token);
  const invitation = invitationQuery.data;

  const openError = useCallback(
    (
      reason: GuardianInvitationErrorReason,
      context?: Omit<InvitationErrorParams, 'token' | 'reason'>,
    ) => {
      navigation.replace('GuardianInvitationError', { token, reason, ...context });
    },
    [navigation, token],
  );

  useEffect(() => {
    if (!invitation) return;
    if (
      invitation.status === GuardianInvitationStatus.Expired ||
      invitation.status === GuardianInvitationStatus.Revoked
    ) {
      openError(
        invitation.status === GuardianInvitationStatus.Expired
          ? GuardianInvitationErrorReason.Expired
          : GuardianInvitationErrorReason.InvalidLink,
        {
          invitedEmail: invitation.invitedEmail,
          inviterName: invitation.invitedByName,
          playerName: invitation.playerName,
          expiryDate: invitation.expiresAt,
        },
      );
    } else if (invitation.status !== GuardianInvitationStatus.Pending) {
      openError(GuardianInvitationErrorReason.AlreadyProcessed);
    }
  }, [invitation, openError]);

  useEffect(() => {
    if (!invitationQuery.error) return;
    const status = getApiStatus(invitationQuery.error);
    if (isInvitationEmailMismatch(invitationQuery.error) || status === HttpStatus.Forbidden) {
      openError(
        hasAuthenticatedSession
          ? GuardianInvitationErrorReason.EmailMismatch
          : GuardianInvitationErrorReason.LoginRequired,
        {
          invitedEmail: getInvitedEmail(invitationQuery.error),
        },
      );
    } else if (status === HttpStatus.Unauthorized) {
      openError(GuardianInvitationErrorReason.LoginRequired, {
        invitedEmail: getInvitedEmail(invitationQuery.error),
      });
    } else if (status === HttpStatus.NotFound) {
      openError(GuardianInvitationErrorReason.InvalidLink, {
        invitedEmail: getInvitedEmail(invitationQuery.error),
      });
    } else {
      openError(GuardianInvitationErrorReason.Unknown);
    }
  }, [hasAuthenticatedSession, invitationQuery.error, openError]);

  const expiryDate = useMemo(() => {
    if (!invitation?.expiresAt) return '';
    const parsed = parseISO(invitation.expiresAt);
    return isValid(parsed) ? format(parsed, DATE_FORMATS.TEXT) : invitation.expiresAt;
  }, [invitation?.expiresAt]);

  const handleActionError = useCallback(
    (error: unknown) => {
      const status = getApiStatus(error);
      if (isInvitationEmailMismatch(error) || status === HttpStatus.Forbidden) {
        openError(GuardianInvitationErrorReason.EmailMismatch, {
          invitedEmail: getInvitedEmail(error),
        });
      } else if (status === HttpStatus.Unauthorized) {
        openError(GuardianInvitationErrorReason.LoginRequired);
      } else if (status === HttpStatus.NotFound) {
        openError(GuardianInvitationErrorReason.InvalidLink);
      } else if (status === HttpStatus.Conflict) {
        openError(GuardianInvitationErrorReason.AlreadyProcessed);
      } else {
        openError(GuardianInvitationErrorReason.Unknown);
      }
    },
    [openError],
  );

  const handleAccept = useCallback(() => {
    if (!hasAuthenticatedSession) {
      openError(GuardianInvitationErrorReason.LoginRequired);
      return;
    }
    if (!invitation) return;
    acceptMutation.mutate(undefined, {
      onSuccess: () => {
        showAlert({
          title: copy.result.acceptTitle,
          message: copy.result.acceptMessage(invitation.playerName),
          buttons: [
            {
              text: copy.result.ok,
              onPress: () =>
                navigation.reset({
                  index: 0,
                  routes: [{ name: 'Guardian', params: { screen: 'GuardianDashboard' } }],
                }),
            },
          ],
        });
      },
      onError: handleActionError,
    });
  }, [
    acceptMutation,
    handleActionError,
    hasAuthenticatedSession,
    invitation,
    navigation,
    openError,
  ]);

  const handleDecline = useCallback(() => {
    if (!hasAuthenticatedSession) {
      openError(GuardianInvitationErrorReason.LoginRequired);
      return;
    }
    declineMutation.mutate(undefined, {
      onSuccess: () => {
        showAlert({
          title: copy.result.declineTitle,
          message: copy.result.declineMessage,
          buttons: [
            {
              text: copy.result.ok,
              onPress: () =>
                navigation.reset({
                  index: 0,
                  routes: [{ name: 'Guardian', params: { screen: 'GuardianDashboard' } }],
                }),
            },
          ],
        });
      },
      onError: handleActionError,
    });
  }, [declineMutation, handleActionError, hasAuthenticatedSession, navigation, openError]);

  return {
    invitation,
    invitedEmail: invitation?.invitedEmail ?? userEmail,
    expiryDate,
    isLoading:
      !isInitialized ||
      invitationQuery.isLoading ||
      !invitation ||
      invitation.status !== GuardianInvitationStatus.Pending,
    submittingAction: acceptMutation.isPending
      ? GuardianInvitationSubmissionAction.Accept
      : declineMutation.isPending
        ? GuardianInvitationSubmissionAction.Decline
        : undefined,
    handleAccept,
    handleDecline,
  };
}
